// src/components/Sidebar.js
import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './Sidebar.module.css';

export default function Sidebar() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const role = userProfile?.role || 'free';
  const [open, setOpen] = useState(false);

  // Close flyout on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when flyout is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = {
    free: [
      { to: '/dashboard',  label: 'Dashboard',      icon: '⬡' },
      { to: '/assessment', label: 'My Assessment',  icon: '◈' },
    ],
    team_leader: [
      { to: '/dashboard',                label: 'Dashboard',                icon: '⬡' },
      { to: '/team',                     label: 'My Team',                  icon: '◎' },
      { to: '/assessment',               label: 'My Assessment',            icon: '◈' },
      { to: '/team-assessment',          label: 'Team Assessment',          icon: '📊' },
      { to: '/team-meetings',            label: 'Meetings',                 icon: '📅' },
      { to: '/consultant-observation',   label: 'Consultant Observation',   icon: '👁' },
      { to: '/working-genius',           label: 'Working Genius',           icon: '✦' },
    ],
    team_member: [
      { to: '/dashboard',                label: 'Dashboard',                icon: '⬡' },
      { to: '/assessment',               label: 'My Assessment',            icon: '◈' },
      { to: '/team-assessment',          label: 'Team Assessment',          icon: '📊' },
      { to: '/team-meetings',            label: 'Meetings',                 icon: '📅' },
      { to: '/consultant-observation',   label: 'Consultant Observation',   icon: '👁' },
      { to: '/working-genius',           label: 'Working Genius',           icon: '✦' },
    ],
    consultant: [
      { to: '/dashboard',              label: 'Dashboard',              icon: '⬡' },
      { to: '/meetings',               label: 'My Teams',               icon: '◐' },
      { to: '/meetings/new',           label: 'New Meeting',            icon: '＋' },
    ],
  };

  // Admins work out of the Admin Panel — team hubs and meetings are reached from there
  const adminLinks = [
    { to: '/admin', label: 'Admin Panel', icon: '⊕' },
  ];

  const links = role === 'admin' ? adminLinks : (navItems[role] || navItems.free);

  const ROLE_DISPLAY = {
    free: 'Solo Assessment',
    team_leader: 'Team Leader',
    team_member: 'Team Member',
    consultant: 'Consultant',
    admin: 'Admin',
  };

  return (
    <>
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="mobile-menu-btn" onClick={() => setOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <img src="/logo-white.png" alt="Meeting.Training" className="mobile-topbar-logo" />
        <div className={styles.spacer} />
      </div>

      {/* Overlay */}
      {open && <div className="sidebar-overlay" onClick={() => setOpen(false)} />}

    <aside className={`sidebar${open ? ' sidebar--open' : ''}`}>
      <div className="sidebar-logo">
        <img src="/logo-white.png" alt="Meeting.Training" />
        <div className="sidebar-logo-tagline">Training Facilitators</div>
        <button className="sidebar-close-btn" onClick={() => setOpen(false)} aria-label="Close menu">✕</button>
      </div>

      <nav>
        <ul className="sidebar-nav">
          {links.map(({ to, label, icon, divider }) => (
            <li key={to}>
              {divider && <div className="sidebar-nav-divider" />}
              <NavLink
                to={to}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span className="nav-icon">{icon}</span>
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <div className={styles.footerMeta}>
          <div className={styles.footerName}>
            {userProfile?.displayName || userProfile?.email?.split('@')[0]}
          </div>
          <span className={styles.footerRole}>
            {ROLE_DISPLAY[role] || role.replace('_', ' ')}
          </span>
        </div>
        <button
          onClick={handleLogout}
          className={`btn btn-secondary ${styles.logoutBtn}`}
        >
          Sign out →
        </button>
      </div>
    </aside>
    </>
  );
}
